import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { X } from 'lucide-react';
import ApiService from '../../components/api_services/apiService.jsx';
import {
  setShowAddModal,
  toggleNeedRefresh,
} from '../../store/notificationsTabSlice';
import './Notifications.css';

function AddNotificationModal() {
  const dispatch = useDispatch();
  const showAddModal = useSelector((state) => state.notifications.showAddModal);

  const [formData, setFormData] = useState({
    title: '',
    message: '',
    type: 'SINGULAR',
    is_recurring: false,
    recurrence_pattern: '',
    scheduled_send_date: '',
    next_send_date: '',
    send_to_all_users: true,
    user_ids: '',
  });
  const [loading, setLoading] = useState(false);

  const handleClose = () => {
    dispatch(setShowAddModal(false));
    setFormData({
      title: '',
      message: '',
      type: 'SINGULAR',
      is_recurring: false,
      recurrence_pattern: '',
      scheduled_send_date: '',
      next_send_date: '',
      send_to_all_users: true,
      user_ids: '',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate recurring pattern if type is RECURRING
    if (formData.type === 'RECURRING' && !formData.recurrence_pattern) {
      alert('Proszę wybrać wzorzec powtarzania dla powiadomienia powtarzającego się.');
      return;
    }
    
    setLoading(true);

    try {
      const payload = {
        title: formData.title,
        message: formData.message,
        type: formData.type,
        send_to_all_users: formData.send_to_all_users,
      };

      if (formData.type === 'RECURRING') {
        payload.is_recurring = true;
        payload.recurrence_pattern = formData.recurrence_pattern;
        // For recurring, next_send_date is optional - API will calculate it if not provided
        if (formData.next_send_date) {
          const date = new Date(formData.next_send_date);
          payload.next_send_date = date.toISOString();
        }
      } else {
        payload.is_recurring = false;
        if (formData.scheduled_send_date) {
          // Convert to ISO 8601 format
          const date = new Date(formData.scheduled_send_date);
          payload.scheduled_send_date = date.toISOString();
        }
      }

      if (!formData.send_to_all_users && formData.user_ids.trim()) {
        payload.user_ids = formData.user_ids
          .split(',')
          .map((id) => id.trim())
          .filter((id) => id.length > 0);
      }

      const campaign = await ApiService.createNotification(payload);
      alert(`Kampania została utworzona! Docelowi użytkownicy: ${campaign.target_user_count || 0}`);
      handleClose();
      dispatch(toggleNeedRefresh());
      // Reload the page to refresh the list
      window.location.reload();
    } catch (error) {
      console.error('Błąd podczas tworzenia powiadomienia:', error);
      alert('Nie udało się utworzyć powiadomienia.');
    } finally {
      setLoading(false);
    }
  };

  if (!showAddModal) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content notification-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={handleClose}>
          <X size={20} />
        </button>
        <h2 className="modal-title">Dodaj powiadomienie</h2>

        <form onSubmit={handleSubmit} className="notification-form">
          <div className="form-field">
            <label>
              Tytuł <span className="required">*</span>
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              maxLength={255}
              placeholder="Wprowadź tytuł powiadomienia"
            />
          </div>

          <div className="form-field">
            <label>
              Wiadomość <span className="required">*</span>
            </label>
            <textarea
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              required
              maxLength={5000}
              rows={5}
              placeholder="Wprowadź treść powiadomienia"
            />
          </div>

          <div className="form-field">
            <label>
              Typ <span className="required">*</span>
            </label>
            <select
              value={formData.type}
              onChange={(e) => {
                const newType = e.target.value;
                setFormData({
                  ...formData,
                  type: newType,
                  is_recurring: newType === 'RECURRING',
                  recurrence_pattern: newType === 'RECURRING' ? formData.recurrence_pattern : '',
                });
              }}
              required
            >
              <option value="SINGULAR">Pojedyncze</option>
              <option value="RECURRING">Powtarzające się</option>
            </select>
          </div>

          {formData.type === 'RECURRING' && (
            <>
              <div className="form-field">
                <label>
                  Wzorzec powtarzania <span className="required">*</span>
                </label>
                <select
                  value={formData.recurrence_pattern}
                  onChange={(e) => setFormData({ ...formData, recurrence_pattern: e.target.value })}
                  required={formData.type === 'RECURRING'}
                >
                  <option value="">Wybierz wzorzec</option>
                  <option value="DAILY">Codziennie</option>
                  <option value="WEEKLY">Co tydzień</option>
                  <option value="MONTHLY">Co miesiąc</option>
                </select>
              </div>
              <div className="form-field">
                <label>Następna data wysłania (opcjonalne)</label>
                <input
                  type="datetime-local"
                  value={formData.next_send_date || ''}
                  onChange={(e) => setFormData({ ...formData, next_send_date: e.target.value })}
                />
                <small style={{ color: '#9AA3AB', fontSize: '11px', marginTop: '4px', display: 'block' }}>
                  Jeśli nie podano, data zostanie obliczona automatycznie
                </small>
              </div>
            </>
          )}

          {formData.type === 'SINGULAR' && (
            <div className="form-field">
              <label>Zaplanowana data wysłania</label>
              <input
                type="datetime-local"
                value={formData.scheduled_send_date}
                onChange={(e) => setFormData({ ...formData, scheduled_send_date: e.target.value })}
              />
            </div>
          )}

          <div className="form-field">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.send_to_all_users}
                onChange={(e) => setFormData({ ...formData, send_to_all_users: e.target.checked })}
              />
              Wyślij do wszystkich użytkowników
            </label>
          </div>

          {!formData.send_to_all_users && (
            <div className="form-field">
              <label>ID użytkowników (oddzielone przecinkami)</label>
              <input
                type="text"
                value={formData.user_ids}
                onChange={(e) => setFormData({ ...formData, user_ids: e.target.value })}
                placeholder="uuid1, uuid2, uuid3"
              />
            </div>
          )}

          <div className="modal-footer">
            <button type="button" className="btn-cancel" onClick={handleClose} disabled={loading}>
              Anuluj
            </button>
            <button type="submit" className="btn-submit" disabled={loading}>
              {loading ? 'Zapisywanie...' : 'Utwórz'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddNotificationModal;

